<script setup lang="ts">
import {useElementor, useWp} from "~/composables/Redmonkey";
import StepsTextFlow from "~/components/Redmonkey/Base/StepsTextFlow.vue";
import TitleBase from "~/components/Redmonkey/Base/TitleBase.vue";
import Item from "~/components/Redmonkey/Events/Item.vue";
import {useSettingsStore} from "~/stores/settings";
import DecorSidebar from "~/components/Redmonkey/Base/DecorSidebar.vue";
import CurvedBlock from "~/components/Redmonkey/Base/CurvedBlock.vue";

const settingsStore = useSettingsStore();

const {
  getSettingsValue,
} = useElementor();

const {
  getMediaAltFromMedia,
  getMediaHeightFromMedia,
  getMediaUrlFromMedia,
  getMediaWidthFromMedia,
  getSettingsValue: getThemeSettingValue
} = useWp();

const props = defineProps({
  event: {
    type: Object,
    required: true
  }
})

const getTitle = () => {
  return getSettingsValue(props.event, 'title') || props.event?.title;
}

const getImage = () => {
  if (getSettingsValue(props.event, 'image', 'url')) {
    return {
      url: getSettingsValue(props.event, 'image', 'url'),
      alt: getSettingsValue(props.event, 'image', 'alt'),
      width: 'auto',
      height: 'auto'
    }
  }

  return {
    url: getMediaUrlFromMedia(props.event?.featuredImage?.node),
    alt: getMediaAltFromMedia(props.event?.featuredImage?.node),
    width: getMediaWidthFromMedia(props.event?.featuredImage?.node),
    height: getMediaHeightFromMedia(props.event?.featuredImage?.node)
  }
}

const getSteps = () => {
  return getSettingsValue(props.event, 'steps');
}

const getDescription = () => {
  return getSettingsValue(props.event, 'description');
}

const getDate = () => {
  if (!props.event?.events?.dateTimeFrom) return false;
  const date = new Date(props.event.events.dateTimeFrom);

  const day = date.getUTCDate();
  const monthNames = [
    "січня", "лютого", "березня", "квітня", "травня", "червня",
    "липня", "серпня", "вересня", "жовтня", "листопада", "грудня"
  ];
  const month = monthNames[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
}

const getTime = () => {
  if (!props.event?.events?.dateTimeFrom || !props.event?.events?.dateTimeTo) return false;

  const date1 = new Date(props.event.events.dateTimeFrom);
  const date2 = new Date(props.event.events.dateTimeTo);

  const hours1 = String(date1.getUTCHours()).padStart(2, '0');
  const minutes1 = String(date1.getUTCMinutes()).padStart(2, '0');

  const hours2 = String(date2.getUTCHours()).padStart(2, '0');
  const minutes2 = String(date2.getUTCMinutes()).padStart(2, '0');

  // Return the formatted time range
  return `${hours1}:${minutes1}-${hours2}:${minutes2}`;
}

const getAddress = () => {
  return props.event?.events?.address;
}

const getCity = () => {
  return props.event?.events?.misto;
}

const getNextEventId = () => {
  return getSettingsValue(props.event, 'choose_event');
}

const getPrice = () => {
  return props.event?.events?.price;
}

const getEventData = async () => {
  if (getNextEventId()) {
    return await $fetch('/api/wordpress/getEvents', {
      method: "post",
      body: {
        first: 1,
        ids: [parseInt(getNextEventId())]
      }
    })
  } else {
    return await $fetch('/api/wordpress/getEvents', {
      method: "post",
      body: {
        first: 1,
        notIds: [props.event.id]
      }
    })
  }
}

const {events} = await getEventData();

const nextEvent = computed(() => events?.nodes?.[0]);
</script>
<template>
  <div class="single-event single">
    <h1 v-if="getTitle()">{{ getTitle() }}</h1>
    <div class="event-intro flex wrap justify">
      <div class="event-info">
        <ul>
          <li class="date-time" v-if="getDate()">Час: <span>{{ `${getDate()} ${getTime() || ''}` }}</span></li>
          <li class="address" v-if="getAddress() || getCity()">Адреса:
            <span>{{ `${getCity() || ''} ${getAddress() || ''}` }}</span></li>
          <li class="price" v-if="getPrice()">Ціна: <span>{{ getPrice() }}</span></li>
        </ul>
      </div>
      <div class="event-img">
        <picture>
          <img v-if="getImage().url" :alt="getImage().alt" :src="getImage().url" :width="getImage().width"
               :height="getImage().height"/>
        </picture>
      </div>
      <div class="event-decor">
        <img src="/images/hexagon.png" :alt="getTitle()"/>
      </div>
    </div>
    <div class="event-steps" v-if="getSteps().length">
      <StepsTextFlow :steps="getSteps()"/>
    </div>
    <div class="event-description" v-if="getDescription()">
      <div class="event-description-content flex wrap">
        <CurvedBlock :content="getDescription()"/>
        <DecorSidebar/>
      </div>
      <div class="event-description-button">
        <nuxt-link class="action secondary" :to="{ hash: '#demo-form' }">
          Записатись
        </nuxt-link>
      </div>
    </div>
    <div class="next-event" v-if="nextEvent">
      <TitleBase title="Наш наступний захід" position="center" star-position="right"/>
      <Item
          :name="nextEvent.title"
          :title="nextEvent.events.title"
          :sub-title="nextEvent.events.subTitle"
          :url="nextEvent.uri"
          :image="nextEvent.featuredImage.node"
          :white-logo="getThemeSettingValue(settingsStore.redmonkeySettings, 'node', 'whiteLogo')"
          :city="nextEvent.events.misto"
          :date-from="nextEvent.events.dateTimeFrom"
          :date-to="nextEvent.events.dateTimeTo"
          :price="nextEvent.events.price"
      />
      <div class="all-events-button">
        <nuxt-link to="/events" class="action primary">
          Більше заходів
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import "assets/css/source/components/single";
</style>